import { useEffect, useRef } from "react";
import { useSession } from "next-auth/react";
import urlJoin from "url-join";

import {
  useDictionaryStore,
  Translation,
  SystemRole,
} from "../store/dictionaryStore";
import { useCartStore } from "../store/cartStore";
import { Categories } from "../models/header";

/** Forma común de estas respuestas: el payload puede venir envuelto en `.data` o plano. */
function unwrap<T>(response: unknown): T | undefined {
  const data = (response as { data?: T } | null)?.data ?? (response as T);
  return data ?? undefined;
}

/**
 * Custom hook to initialize all dictionary data from the backend.
 * This hook is intended to be called at the top-level MainLayout.
 */
export function useDictionaryInitializer() {
  const { data: session } = useSession();
  const {
    accountRole,
    setAccountRole,
    translationsType,
    setTranslationsType,
    categoriesMenu,
    setCategoriesMenu,
    systemRoles,
    setSystemRoles,
    exchangeRate,
    setExchangeRate,
  } = useDictionaryStore();

  const { setExchangeRate: setExchangeRateCart } = useCartStore();

  const cacheRef = useRef<{
    [key: string]: { timestamp: number; inProgress: boolean };
  }>({});
  const CACHE_DURATION = 15 * 60 * 1000; // 15 minutos en milisegundos

  useEffect(() => {
    const now = Date.now();

    const isStale = (key: string) => {
      const entry = cacheRef.current[key];
      return !entry || now - entry.timestamp > CACHE_DURATION;
    };

    const setInProgress = (key: string, value: boolean) => {
      if (!cacheRef.current[key])
        cacheRef.current[key] = { timestamp: 0, inProgress: false };
      cacheRef.current[key].inProgress = value;
    };

    const setTimestamp = (key: string) => {
      if (!cacheRef.current[key])
        cacheRef.current[key] = { timestamp: 0, inProgress: false };
      cacheRef.current[key].timestamp = Date.now();
    };

    const isInProgress = (key: string) => cacheRef.current[key]?.inProgress;

    if (
      (categoriesMenu.length === 0 || isStale("categories")) &&
      !isInProgress("categories")
    ) {
      setInProgress("categories", true);
      fetch(`/api/search/categories/menu-tree`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        cache: "force-cache",
        next: {
          tags: ["Categories"],
          revalidate: CACHE_DURATION,
        },
      })
        .then((res) => {
          if (!res.ok) return undefined;
          return res.json();
        })
        .then((response: unknown) => {
          if (!response) return;
          const data = unwrap<Categories[]>(response);
          if (Array.isArray(data)) {
            setCategoriesMenu(data);
            setTimestamp("categories");
          }
        })
        .catch(() => {})
        .finally(() => {
          setInProgress("categories", false);
        });
    }

    // 2. Fetch Relation Contexts/producto Translations (Público/Opcional Auth)
    if (
      (translationsType.length === 0 || isStale("translations")) &&
      !isInProgress("translations")
    ) {
      setInProgress("translations", true);
      fetch(`/api/products/relation-contexts`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        cache: "force-cache",
        next: {
          tags: ["Translations"],
          revalidate: CACHE_DURATION,
        },
      })
        .then((res) => {
          if (!res.ok) return undefined;
          return res.json();
        })
        .then((response: unknown) => {
          if (!response) return;
          const data = unwrap<Translation[]>(response);
          if (Array.isArray(data)) {
            setTranslationsType(data);
            setTimestamp("translations");
          }
        })
        .catch(() => {})
        .finally(() => {
          setInProgress("translations", false);
        });
    }

    if (
      (exchangeRate === null || isStale("exchangeRate")) &&
      !isInProgress("exchangeRate")
    ) {
      setInProgress("exchangeRate", true);
      fetch(
        urlJoin(
          process.env.NEXT_PUBLIC_API_URL || "",
          "/pricing/exchange-rate",
        ),
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        },
      )
        .then((res) => {
          if (!res.ok) return undefined;
          return res.json();
        })
        .then((response: unknown) => {
          if (!response) return;
          const rate = unwrap<{ rate?: number }>(response)?.rate;
          if (typeof rate === "number") {
            setExchangeRate(rate);
            setExchangeRateCart(rate);
            setTimestamp("exchangeRate");
          }
        })
        .catch(() => {})
        .finally(() => {
          setInProgress("exchangeRate", false);
        });
    }

    const accessToken = session?.user?.accessToken;
    if (accessToken) {
      if (
        (accountRole.length === 0 || isStale("accountRoles")) &&
        !isInProgress("accountRoles")
      ) {
        setInProgress("accountRoles", true);
        fetch(
          urlJoin(
            process.env.NEXT_PUBLIC_API_URL_MANAGEMENT || "",
            "identity/accounts/account-roles",
          ),
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
            cache: "force-cache",
            next: {
              tags: ["AccountRoles"],
              revalidate: CACHE_DURATION,
            },
          },
        )
          .then((res) => {
            if (!res.ok) return undefined;
            return res.json();
          })
          .then((response: unknown) => {
            if (!response) return;
            const roles = unwrap<Translation[]>(response);
            if (Array.isArray(roles)) {
              setAccountRole(roles);
              setTimestamp("accountRoles");
            }
          })
          .catch(() => {})
          .finally(() => {
            setInProgress("accountRoles", false);
          });
      }

      if (
        (systemRoles.length === 0 || isStale("systemRoles")) &&
        !isInProgress("systemRoles")
      ) {
        setInProgress("systemRoles", true);
        fetch(
          urlJoin(
            process.env.NEXT_PUBLIC_API_URL_MANAGEMENT ||
              process.env.NEXT_PUBLIC_API_URL ||
              "",
            "identity/users/system-roles",
          ),
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
            cache: "force-cache",
            next: {
              tags: ["SystemRoles"],
              revalidate: CACHE_DURATION,
            },
          },
        )
          .then((res) => {
            if (!res.ok) return undefined;
            return res.json();
          })
          .then((response: unknown) => {
            if (!response) return;
            const data = unwrap<SystemRole[]>(response);
            if (Array.isArray(data)) {
              setSystemRoles(data);
              setTimestamp("systemRoles");
            }
          })
          .catch(() => {})
          .finally(() => {
            setInProgress("systemRoles", false);
          });
      }
    }
  }, [
    session,
    categoriesMenu.length,
    translationsType.length,
    exchangeRate,
    accountRole.length,
    systemRoles.length,
  ]);
}
